import * as GC from '@grapecity/spread-sheets';
import { FontSizeType } from '@models/types/font-size-type';
import { SettingsService } from '@services/settings/settings.service';

const FONT_FAMILY = 'Calibri';
const spreadsheetBackgroundColor = '#ffffff';
const grayAreaBackColor = '#ffffff';
const disabledCellBackColor = 'Background 2';

/**
 * Obtains a formatter for excel using the following rules
 * @param currencySymbol The text representation of a symbol ('$', '£', ...). Can be empty if no symbol is required
 * @param customFormatter A string representing the excel formatter to be used. String needs to be semi-colon separated formats
 *  for positive;negative;zero;default. Any of the parameters can be optional, as well as using a custom formatter.
 * Bypasses roundEnabled, can bypass displayZeroAs if ;zero; formatter is passed
 * @param roundEnabled Enables the rounding to the nearest whole number or 2 decimal points if false
 */
const getAccountingFormatter = (
  currencySymbol: string = '',
  customFormatter: string = null,
  roundEnabled: boolean = false
) => {
  const format = customFormatter || (roundEnabled ? '#,##0' : '#,##0.00');
  let displayPositiveAs;
  let displayNegativeAs;
  // Default the positive and negative formats as the default customFormatter format and/or rounding format
  displayPositiveAs = displayNegativeAs = format;

  // Parses the custom formatter as per excel syntax: positiveFormat ; negativeFormat ; formatForZeroes ; defaultFormat (@ by default)
  if (customFormatter) {
    const breakFormats = customFormatter.split(';');
    switch (breakFormats.length) {
      // case 3:
      //   displayZeroAs = breakFormats[2];
      /* falls through */
      case 2:
        displayNegativeAs = breakFormats[1];
      /* falls through */
      case 1:
        displayPositiveAs = breakFormats[0];
        break;
      default:
        throw new SyntaxError(
          'Unable to parse custom formatter. Please use the format positive;negative;zero;default. Optionals parameters allowed'
        );
    }
  }

  const separator = `* `;
  // ! Red/Black colours are set-up here
  const formatForDefault = `@`;
  const formatForPositiveNumbers = `[Black]${currencySymbol}${separator}${displayPositiveAs}`;
  const formatForNegativeNumbers = `[Red]${currencySymbol}${separator}(${displayNegativeAs})`;
  // ! Related defect 3821
  const formatForZero = `${separator}0`; // const formatForZero = `${separator}0${spreadsheetConfig.displayZeroAs}`;

  // Builds a new formatter based on the previous values
  // as per excel syntax: positiveFormat ; negativeFormat ; formatForZeroes ; defaultFormat (@ by default)
  const formatters = [formatForPositiveNumbers, formatForNegativeNumbers, formatForZero, formatForDefault].join(';');
  return formatters;
};

const getDateFormatter = (isAsOfDate = false) => {
  // ! Preserve this exact format for T3440 & D4122
  const shortDate = `mm/dd/yy;@`;
  const longDate = `[$-${SettingsService.locale}]mmmm d, yyyy;@`;
  const format = isAsOfDate ? longDate : shortDate;
  return format;
};

// Part of task 3443
export const getFont = (size: FontSizeType = 'md', bold = false, italic = false) => {
  let fontSize: number;
  const defaultSize = 13;
  switch (size) {
    case 'xx-sm':
      fontSize = 10;
      break;
    case 'x-sm':
      fontSize = 11;
      break;
    case 'sm':
      fontSize = 12;
      break;
    case 'md':
      fontSize = defaultSize;
      break;
    case 'lg':
      fontSize = 15;
      break;
    case 'x-lg':
      fontSize = 16;
      break;
    case 'xx-lg':
      fontSize = 18;
      break;
    default:
      fontSize = defaultSize;
  }
  return `${bold ? 'bold ' : ''} ${italic ? 'italic ' : ''}${fontSize}pt ${FONT_FAMILY}`.trim();
};

export const spreadsheetConfig = {
  // Fix 3532 Part 8 (gray out the non-spreadsheet area to make It look as a paper)
  grayAreaBackColor,
  backColor: spreadsheetBackgroundColor,
  disabledCellBackColor,
  hostStyle: {
    width: '100%',
    height: '100%'
  },
  failBackColor: 'PINK', // light-red
  passBackColor: 'rgb(198, 239, 206)', // light green
  warningBackColor: 'MOCCASIN',
  chartsColumnBackColor: 'aliceblue',
  containerParentId: 'panel-main-body',
  excelTextSeparators: {
    // ! Do not touch the excelTextSeparators at all (Defect 3185)
    row: '\n',
    col: '\t'
  },
  align: {
    left: (function() {
      const instance = new GC.Spread.Sheets.Style();
      instance.hAlign = GC.Spread.Sheets.HorizontalAlign.left;
      return instance;
    })(),
    right: (function() {
      const instance = new GC.Spread.Sheets.Style();
      instance.hAlign = GC.Spread.Sheets.HorizontalAlign.right;
      return instance;
    })(),
    center: (function() {
      const instance = new GC.Spread.Sheets.Style();
      instance.hAlign = GC.Spread.Sheets.HorizontalAlign.center;
      return instance;
    })()
  },
  fonts: {
    family: FONT_FAMILY,
    getFont
  },
  displayZeroAs: '-',
  formatters: {
    getAccountingFormatter,
    getDateFormatter
  }
};
